import {
  createSlice,
  createEntityAdapter,
  EntityState,
  createSelector,
} from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import { fetchCaseInfo } from "../../../thunks/Case";
import { addParticipant, updateParticipant } from "./thunk";
import { ParticipantType, Participation } from "../../../../src/api/participants";
import { updateCaseStage } from "../../caseStages/logic/thunks";

const participantAdapter = createEntityAdapter<
  Participation,
  string
>({
  selectId: (p) => p.participation_id,
});


interface ParticipantState {
  participants: EntityState<Participation, string>;
  loadingStatus: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  isUpdating: boolean;
}

const initialState: ParticipantState = {
  participants: participantAdapter.getInitialState(),
  loadingStatus: "idle",
  error: null,
  isUpdating: false,
};



const participantSlice = createSlice({
  name: "participants",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseInfo.pending, (state) => {
        state.loadingStatus = "pending";
        state.error = null;
        participantAdapter.removeAll(state.participants);
      })

      .addCase(fetchCaseInfo.fulfilled, (state, action) => {
        state.loadingStatus = "succeeded";
        const caseData = action.payload.cases?.[0];
        const participations = caseData?.participations ?? [];
        participantAdapter.setAll(state.participants, participations);
      })

      .addCase(fetchCaseInfo.rejected, (state, action) => {
        state.loadingStatus = "failed";
        state.error = "Failed to fetch participants";
      })

      .addCase(addParticipant.pending, (state) => {
        state.isUpdating = true;
      })

      .addCase(addParticipant.fulfilled, (state, action) => {
        participantAdapter.addOne(state.participants, action.payload);
        state.isUpdating=false;
      })

      .addCase(addParticipant.rejected, (state) => {
        state.isUpdating = false;
      })

      .addCase(updateParticipant.pending, (state) => {
        state.isUpdating = true;
      })      

      .addCase(updateParticipant.fulfilled, (state, action) => {
        const { participationId, request } = action.meta.arg;

        participantAdapter.updateOne(state.participants, {
            id: participationId,
            changes: {
            participant_id: request.participant_id,
            },
        });
        state.isUpdating=false;
      })

      .addCase(updateParticipant.rejected, (state) => {
        state.isUpdating = false;
      })   
      
      .addCase(updateCaseStage.fulfilled, (state, action) => {
          const participations = action.payload?.participants;

          if (participations?.length){
            participantAdapter.upsertMany(state.participants, participations); 
          }        
    });
  },
});

const participantSelectors = participantAdapter.getSelectors<RootState>(
  (state) => state.participants.participants
);

export const selectAllParticipants = participantSelectors.selectAll;

export const selectParticipantById = participantSelectors.selectById;

export const selectParticipantByType = createSelector(
  [
    selectAllParticipants,
    (_: RootState, type: ParticipantType) => type,
  ],
  (participants, type) =>
    participants.find((p) => p.participant_type === type)
);

export const selectParticipantsIsUpdating = (state: RootState) =>
  state.participants.isUpdating;

export const participantReducer = participantSlice.reducer;